import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import debounce from 'lodash.debounce';
import QuickSearchResult from './QuickSearchResult';
import { translate } from '../Services/translation';
import {
    query,
    setSearchQuery,
    toggleShowFullForm,
    handleClickSearch,
    selectItem,
} from '../Actions/QuickSearch.action';

// debouncing function to 200ms so we don't send query request on every key stroke
const debouncedQuery = debounce((dispatch, text) => dispatch(query(text)), 200);

const QuickSearch = () => {
    const { query, result, showResult, showFullForm } = useSelector(
        (state) => state.quickSearch
    );
    const [lastClickedNode, setLastClickedNode] = useState(null);
    const searchUrl =
        window.__litium.quickSearchUrl +
        (query.length > 0 ? `?q=${query}` : '');
    const searchContainer = useRef(null);
    const searchInput = useRef(null);
    const dispatch = useDispatch();
    const [previousMatch, setPreviousMatch] = useState(false);
    const [showResultContainer, setShowResultContainer] = useState(false);

    const clickHandler = useCallback(
        (event) => {
            if (searchContainer?.current) {
                setShowResultContainer(
                    searchContainer.current.contains(event.target)
                );
            } else setShowResultContainer(true);
            setLastClickedNode(event.target);
        },
        [showResult]
    );

    const handleKeyDown = (event) => {
        if (event.keyCode === 13 && query.length > 0) {
            location.href = searchUrl;
        }
    };

    useEffect(() => {
        // listen for click event to hide the search when clicking outside
        window.addEventListener('mousedown', clickHandler);
        return () => window.removeEventListener('mousedown', clickHandler);
    }, [clickHandler]);

    useEffect(() => {
        if (result && result[0] && result[0].exactMatch) {
            dispatch(selectItem(0));
            setPreviousMatch(true);
        } else if (previousMatch && !result[0].exactMatch) {
            dispatch(selectItem(-1)); // resets selected item to be able to navigate to search page
            setPreviousMatch(false);
        }
    }, [result, dispatch, previousMatch]);

    useEffect(() => {
        // set query value if it is avaialble in the Url
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('q')) {
            const query = urlParams.get('q');
            dispatch(setSearchQuery(query));
        }
    }, [dispatch]);

    return (
        <div className="quick-search" role="search" ref={searchContainer}>
            <a
                className="quick-search__link--block hide-desktop"
                rel="nofollow"
                href="#"
                title={translate('general.search')}
                onClick={(e) => {
                    e.preventDefault();
                    dispatch(toggleShowFullForm());
                    setTimeout(() => {
                        searchInput.current && searchInput.current.focus();
                    }, 0);
                }}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none">
                    <g clipPath="url(#clip0_7480_10632)">
                        <path d="M8.1013 15.1753C11.9998 15.1753 15.1601 12.0149 15.1601 8.11644C15.1601 4.21796 11.9998 1.05762 8.1013 1.05762C4.20282 1.05762 1.04248 4.21796 1.04248 8.11644C1.04248 12.0149 4.20282 15.1753 8.1013 15.1753Z" stroke="var(--navigation-icon-color, #FFFFFF)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16.9248 16.9398L13.0865 13.1016" stroke="var(--navigation-icon-color, #FFFFFF)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </g>
                    <defs>
                        <clipPath id="clip0_7480_10632">
                            <rect width="18" height="18" fill="white" transform="translate(0.275269 -0.000976562)"/>
                        </clipPath>
                    </defs>
                </svg>
            </a>
            <div
                className={`quick-search__form ${
                    showFullForm ? 'quick-search__form--force-show' : ''
                }`}
                role="search"
            >
                <input
                    className="quick-search__input"
                    type="search"
                    placeholder={translate('general.search')}
                    autoComplete="off"
                    value={decodeURIComponent(query)}
                    onChange={(event) => {
                        const text = encodeURIComponent(event.target.value);
                        dispatch(setSearchQuery(text));
                        debouncedQuery(dispatch, text);
                    }}
                    onKeyDown={(event) => handleKeyDown(event)}
                    ref={searchInput}
                    onBlur={() => {
                        if (
                            searchContainer.current &&
                            !searchContainer.current.contains(lastClickedNode)
                        ) {
                            showFullForm && dispatch(toggleShowFullForm());
                        }
                    }}
                />
                <button
                    className="quick-search__submit-button"
                    type="button"
                    aria-label={translate('general.search')}
                    onClick={(e) => {
                        e.preventDefault();
                        dispatch(handleClickSearch({ searchUrl }));
                    }}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none">
                    <g clipPath="url(#clip0_7480_10632)">
                        <path d="M8.1013 15.1753C11.9998 15.1753 15.1601 12.0149 15.1601 8.11644C15.1601 4.21796 11.9998 1.05762 8.1013 1.05762C4.20282 1.05762 1.04248 4.21796 1.04248 8.11644C1.04248 12.0149 4.20282 15.1753 8.1013 15.1753Z" stroke="var(--navigation-icon-color, #FFFFFF)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16.9248 16.9398L13.0865 13.1016" stroke="var(--navigation-icon-color, #FFFFFF)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </g>
                    <defs>
                        <clipPath id="clip0_7480_10632">
                            <rect width="18" height="18" fill="white" transform="translate(0.275269 -0.000976562)"/>
                        </clipPath>
                    </defs>
                </svg>
                    <span className="quick-search__submit-title">
                        {translate('general.search')}
                    </span>
                </button>
                {showResultContainer && (
                    <QuickSearchResult result={result} searchUrl={searchUrl} />
                )}
            </div>
        </div>
    );
};

export default QuickSearch;
